/**
 * WARDFlex® Sizing Table Generator - Core Calculation Logic
 * Calculates gas piping capacity tables for natural gas and propane systems
 */

class WARDFlexCalculator {
  constructor() {
    // Calculation constants - these are the core mathematical tables
    this.table1_MO = [
      0.000032176678956, 0.0000052398372787, 0.000001066460927,
      0.00000061064840408, 0.0000000491541, 0.0000000187, 0.00000000229
    ];

    this.table1_MI = [
      1.936585409, 2.0115878376, 2.044659002, 1.950149986, 2.0766, 2.0286, 2.12146
    ];

    this.table1_C = [1, 1, 1, 1, 0.800312012, 1, 1];

    this.table2_MO = [
      0, 0.0000064935, 0.0000008986, 0.0000002262, 0.0000000302,
      0.0000000148, 0.0000000042
    ];

    this.table2_MI = [
      0, 1.96485767, 2.08652419, 2.09352175, 2.1301052614, 2.04736781, 2.05557668
    ];

    this.table2_C = [1, 1, 1, 1, 1, 1, 1];

    // Special pressure/drop combinations with pre-calculated coefficients
    this.table3 = [
      {
        supplyPressure: 0.5,
        pressureDrop: 0.5,
        A: [0, 389.95, 380.73, 1024.4, 2253.8, 3356.6, 7347.8],
        B: [0, -0.57, -0.391, -0.463, -0.454, -0.415, -0.453]
      },
      {
        supplyPressure: 0.5,
        pressureDrop: 3,
        A: [0, 663.5, 1435.2, 3260, 4570.6, 9739.8, 17971],
        B: [0, -0.468, -0.497, -0.532, -0.42, -0.461, -0.46]
      },
      {
        supplyPressure: 0.5,
        pressureDrop: 6,
        A: [0, 982.1, 1864.1, 3288.8, 5667.4, 10958, 25840],
        B: [0, -0.483, -0.481, -0.467, -0.4, -0.4, -0.469]
      },
      {
        supplyPressure: 2,
        pressureDrop: 1.5 * 27.73,
        A: [0, 2410.4, 4944.1, 9799, 14137, 33006, 64833],
        B: [0, -0.47, -0.493, -0.508, -0.406, -0.443, -0.463]
      },
      {
        supplyPressure: 5,
        pressureDrop: 3.5 * 27.73,
        A: [0, 3963.7, 6476.6, 14421, 25130, 45707, 86958],
        B: [0, -0.485, -0.454, -0.489, -0.427, -0.414, -0.426]
      }
    ];

    // Tubing size specifications
    this.tubingSizes = [
      { name: "10A (3/8\") 15", size: "10A", inches: "3/8\"", ehd: 15 },
      { name: "15A (1/2\") 19", size: "15A", inches: "1/2\"", ehd: 19 },
      { name: "20A (3/4\") 25", size: "20A", inches: "3/4\"", ehd: 25 },
      { name: "25A (1\") 30", size: "25A", inches: "1\"", ehd: 30 },
      { name: "32A (1-1/4\") 37", size: "32A", inches: "1-1/4\"", ehd: 37 },
      { name: "38A (1-1/2\") 48", size: "38A", inches: "1-1/2\"", ehd: 48 },
      { name: "50A (2\") 62", size: "50A", inches: "2\"", ehd: 62 }
    ];
  }

  /**
   * Validates input parameters
   * @param {Object} params - Input parameters
   * @returns {Object} - Validation result with isValid boolean and error message
   */
  validateInputs(params) {
    const {
      gasType,
      gasPressure,
      gasPressureUnit,
      pressureDrop,
      pressureDropUnit,
      minLength,
      maxLength,
      lengthIncrements
    } = params;

    // Check for required fields
    if (!gasType || gasPressure === undefined || gasPressure === null ||
        pressureDrop === undefined || pressureDrop === null ||
        !minLength || !maxLength || !lengthIncrements) {
      return { isValid: false, error: "All fields required" };
    }

    // Convert pressures to PSI for validation
    const gasPressurePsi = gasPressureUnit === 'inchesWC' ? gasPressure / 27.73 : gasPressure;
    const pressureDropPsi = pressureDropUnit === 'inchesWC' ? pressureDrop / 27.73 : pressureDrop;

    // Validation rules
    if (gasPressurePsi > 5) {
      return { isValid: false, error: "Supply pressure should be no greater than 5 psi" };
    }

    if (gasPressurePsi <= pressureDropPsi) {
      return { isValid: false, error: "Supply pressure must be larger than pressure drop" };
    }

    if (minLength > maxLength) {
      return { isValid: false, error: "Minimum Length must be no greater than Maximum Length" };
    }

    if (maxLength > 2000) {
      return { isValid: false, error: "Maximum Length can not be greater than 2000" };
    }

    if (gasPressure <= 0 || pressureDrop <= 0 || minLength <= 0 || maxLength <= 0 || lengthIncrements <= 0) {
      return { isValid: false, error: "Invalid Value, cannot be 0" };
    }

    return { isValid: true };
  }

  /**
   * Generates sizing table for gas piping
   * @param {Object} params - Calculation parameters
   * @returns {Object} - Results with transposed table data and metadata
   */
  calculateSizingTable(params) {
    const validation = this.validateInputs(params);
    if (!validation.isValid) {
      return { success: false, error: validation.error };
    }

    const {
      gasType,
      gasPressure,
      gasPressureUnit,
      pressureDrop,
      pressureDropUnit,
      minLength,
      maxLength,
      lengthIncrements
    } = params;

    // Convert units to consistent format
    const gasPressurePsi = gasPressureUnit === 'inchesWC' ? gasPressure / 27.73 : gasPressure;
    const pressureDropInches = pressureDropUnit === 'psi' ? pressureDrop * 27.73 : pressureDrop;

    // Check if we have a special table3 case
    const table3Match = this.table3.find(entry =>
      entry.supplyPressure === gasPressurePsi &&
      entry.pressureDrop === pressureDropInches
    );

    // Generate length array for column headers
    const lengths = [];
    for (let currentLength = minLength; currentLength <= maxLength; currentLength += lengthIncrements) {
      lengths.push(currentLength);
    }

    // Ensure maxLength is included if it's not already in the array
    if (lengths[lengths.length - 1] !== maxLength) {
      lengths.push(maxLength);
    }

    // Generate table data - TRANSPOSED: rows = lengths, columns = tubing sizes
    const tableData = [];

    // First row: header with "Tubing Size EHD" and tubing size names
    const headerRow = ["Tubing Size EHD", ...this.tubingSizes.map(size => size.name)];
    tableData.push(headerRow);

    // Subsequent rows: length value + capacities for each tubing size
    for (let j = 0; j < lengths.length; j++) {
      const row = [lengths[j]];

      for (let i = 0; i < this.table1_MO.length; i++) {
        const capacity = this.calculateCapacityForSize(
          i, lengths[j], pressureDrop, pressureDropUnit,
          gasType, table3Match
        );
        row.push(capacity);
      }

      tableData.push(row);
    }

    // Generate header and footer text based on gas type
    const headerText = gasType === 'NaturalGas'
      ? "Maximum Capacity of WARDFLEX®/WARDFLEX® MAX CSST in Cubic Feet per Hour (CFH) of Natural Gas (based on a 0.60 specific gravity gas)"
      : "Maximum Capacity of WARDFLEX®/WARDFLEX® MAX CSST in Thousands of BTU (KBTU) for Propane Gas (LPG) (based on a 1.52 specific gravity gas)";

    const footerText = "Tables include losses for four 90° bends and two end fittings. To compute flow capacity for tubing runs with a larger number of bends and/or fittings, add the appropriate number of feet to the actual run length using the following formula: L = 1.3 (n). L = Number of feet to be added to actual run length. n = Number of Bends and/or fittings over six";

    return {
      success: true,
      data: tableData,
      metadata: {
        gasType,
        gasPressure,
        gasPressureUnit,
        pressureDrop,
        pressureDropUnit,
        minLength,
        maxLength,
        lengthIncrements,
        lengths,
        units: gasType === 'NaturalGas' ? 'CFH' : 'KBTU',
        tubingSizes: this.tubingSizes,
        headerText,
        footerText
      }
    };
  }

  /**
   * Calculate capacity for a specific tubing size
   */
  calculateCapacityForSize(sizeIndex, length, pressureDrop, pressureDropUnit, gasType, table3Match) {
    if (sizeIndex === 0) {
      // Special handling for first size (10A) - use only table1
      const table1Calc = this.calculateWithTable(1, sizeIndex, length, pressureDrop, pressureDropUnit);
      let capacity = Math.trunc(table1Calc);

      // Apply propane conversion if needed
      if (gasType === 'Propane') {
        capacity = Math.trunc(capacity * ((2516 * Math.pow(0.6 / 1.52, 0.5)) / 1000));
      }

      return capacity;
    }

    // For other sizes, use max of multiple calculations
    const table1Calc = this.calculateWithTable(1, sizeIndex, length, pressureDrop, pressureDropUnit);
    const table2Calc = this.calculateWithTable(2, sizeIndex, length, pressureDrop, pressureDropUnit);
    let maxCapacity = Math.max(table1Calc, table2Calc);

    // Add table3 calculation if applicable
    if (table3Match) {
      const table3Calc = table3Match.A[sizeIndex] * Math.pow(length, table3Match.B[sizeIndex]);
      maxCapacity = Math.max(maxCapacity, table3Calc);
    }

    let capacity = Math.trunc(maxCapacity);

    // Apply propane conversion if needed
    if (gasType === 'Propane') {
      capacity = Math.trunc(capacity * ((2516 * Math.pow(0.6 / 1.52, 0.5)) / 1000));
    }

    return capacity;
  }

  /**
   * Calculate using specific table (1 or 2)
   */
  calculateWithTable(tableNum, sizeIndex, length, pressureDrop, pressureDropUnit) {
    let mo, mi, c;

    if (tableNum === 1) {
      mo = this.table1_MO[sizeIndex];
      mi = this.table1_MI[sizeIndex];
      c = this.table1_C[sizeIndex];
    } else {
      mo = this.table2_MO[sizeIndex];
      mi = this.table2_MI[sizeIndex];
      c = this.table2_C[sizeIndex];
    }

    // Handle pressure drop unit conversion
    const pressureValue = pressureDropUnit === 'psi' ? (pressureDrop * 27.73) : pressureDrop;

    return Math.pow(pressureValue / length / mo, 1 / mi) * c;
  }
}

// Export the class for use in other modules
export { WARDFlexCalculator };

// Example usage:
const calculator = new WARDFlexCalculator();

const exampleParams = {
  gasType: 'NaturalGas', // 'NaturalGas' or 'Propane'
  gasPressure: 2,
  gasPressureUnit: 'psi', // 'psi' or 'inchesWC'
  pressureDrop: 0.5,
  pressureDropUnit: 'psi', // 'psi' or 'inchesWC'
  minLength: 10,
  maxLength: 50,
  lengthIncrements: 20
};

const result = calculator.calculateSizingTable(exampleParams);

if (result.success) {
  console.log("Header Text:", result.metadata.headerText);
  console.log("Table Data:", result.data);
  console.log("Footer Text:", result.metadata.footerText);

  // Table structure:
  // result.data[0] = ["Tubing Size EHD", 10, 30, 50] (header row)
  // result.data[1] = ["10A (3/8\") 15", 247, 140, 107] (first tubing size)
  // result.data[2] = ["15A (1/2\") 19", 515, 294, 227] (second tubing size)
  // ... etc
} else {
  console.error("Error:", result.error);
}